import { useFilters } from "../useFilters";
import { searchWithFilters, createListQuery } from "../../services/api/search";
import { useState, useEffect } from "react";
import { t } from "i18next";

/**
 * Hook spécifique pour gérer les filtres des workorders
 * @param resetPageCallback - Fonction pour réinitialiser la pagination
 * @returns - Interface pour manipuler les filtres
 */
export function useWorkordersFilters(resetPageCallback: () => void) {
  const [typeNames, setTypeNames] = useState<Map<string, string>>(new Map());
  const [originNames, setOriginNames] = useState<Map<string, string>>(new Map());

  // Fonction pour transformer les filtres avant sérialisation
  const transformForSerialization = (filters: Partial<EasyTech.WorkordersFilterType>) => {
    const filtersCopy = { ...filters };
    
    // Traitement spécial pour les dates
    if (filtersCopy.dateRange) {
      if (!filtersCopy.dateRange.start && !filtersCopy.dateRange.end) {
        delete filtersCopy.dateRange;
      } else {
        if (filtersCopy.dateRange.start) {
        const startValue = filtersCopy.dateRange.start as any;
        const dateString = startValue instanceof Date 
          ? startValue.toISOString()
          : new Date(startValue).toISOString();
        filtersCopy.dateRange = {
          ...filtersCopy.dateRange,
          start: dateString as any
        };
      }
      if (filtersCopy.dateRange.end) {
        const endValue = filtersCopy.dateRange.end as any;
        const dateString = endValue instanceof Date
          ? endValue.toISOString()
          : new Date(endValue).toISOString();
        filtersCopy.dateRange = {
          ...filtersCopy.dateRange,
          end: dateString as any
        };
      }
      }
    }
    return filtersCopy;
  };
  
  // Fonction pour transformer les filtres après désérialisation
  const transformForDeserialization = (decodedFilters: any): Partial<EasyTech.WorkordersFilterType> => {
    const filters = { ...decodedFilters };
    
    // Traitement spécial pour les dates - on les garde en format string
    // pour éviter les erreurs de type, elles seront converties en Date au moment
    // de leur utilisation dans les requêtes API
    if (filters.dateRange) {
      if (filters.dateRange.start) {
        // Garder en string, pas de conversion en Date pour éviter les erreurs de type
        filters.dateRange.start = filters.dateRange.start as any;
      }
      
      if (filters.dateRange.end) {
        // Garder en string, pas de conversion en Date pour éviter les erreurs de type
        filters.dateRange.end = filters.dateRange.end as any;
      }
    }
    
    if (filters.technician && Array.isArray(filters.technician)) {
      filters.technician = filters.technician.filter((id: any) => !!id);
    }
    if (filters.customer && Array.isArray(filters.customer)) {
      filters.customer = filters.customer.filter((id: any) => !!id);
    }
    return filters;
  };

  // Fonction pour transformer les filtres pour l'affichage (synchrone)
  const transformForDisplay = (filters: Partial<EasyTech.WorkordersFilterType>): Record<string, any> => {
    const displayFilters: Record<string, any> = {};
    
    // Copier tous les filtres sauf "type", "origin" et "missingPurchaseOrder"
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== "type" && key !== "origin" && key !== "missingPurchaseOrder") {
        if (key === "dateRange") {
          const dateRangeValue = value as { start?: any, end?: any };
          if (!dateRangeValue?.start && !dateRangeValue?.end) {
            return;
          }
        }
        displayFilters[key] = value;
      }
    });
    
    // Traitement spécial pour missingPurchaseOrder
    if (filters.missingPurchaseOrder) {
      displayFilters.missingPurchaseOrder = t("workorder:filters:missingPurchaseOrder:badge");
    }
    
    // Si on a des filtres de type, utiliser les noms stockés
    if (filters.type) {
      const typeIds = Array.isArray(filters.type) ? filters.type : [filters.type];
      
      if (Array.isArray(filters.type)) {
        displayFilters.workorderType = typeIds.map(id => {
          const typeName = typeNames.get(id as string);
          return typeName || id; // Garder l'ID si le nom n'est pas trouvé
        });
      } else {
        const typeName = typeNames.get(filters.type as string);
        displayFilters.workorderType = typeName || filters.type;
      }
    }
    
    // Si on a des filtres d'origine, utiliser les noms stockés
    if (filters.origin) {
      const originIds = Array.isArray(filters.origin) ? filters.origin : [filters.origin];
      
      if (Array.isArray(filters.origin)) {
        displayFilters.workorderOrigin = originIds.map(id => {
          const originName = originNames.get(id as string);
          return originName || id; // Garder l'ID si le nom n'est pas trouvé
        });
      } else {
        const originName = originNames.get(filters.origin as string);
        displayFilters.workorderOrigin = originName || filters.origin;
      }
    }
    
    return displayFilters;
  };

  const result = useFilters<EasyTech.WorkordersFilterType>(
    resetPageCallback, 
    {
      transformForSerialization,
      transformForDeserialization,
      transformForDisplay
    }
  );

  // Effet pour charger les noms des types quand les filtres changent
  useEffect(() => {
    const loadTypeNames = async () => {
      if (result.filters?.type) {
        try {
          const typeIds = Array.isArray(result.filters.type) ? result.filters.type : [result.filters.type];
          const validTypeIds = typeIds.filter(id => id && typeof id === "string");
          
          if (validTypeIds.length > 0) {
            const response = await searchWithFilters<EasyTech.WorkorderType>("workorder_types", {
              queries: [createListQuery("id", validTypeIds, "IN")]
            }, true, false);
            
            const types = response.data as EasyTech.WorkorderType[];
            const newTypeMap = new Map(types.map(type => [type.id, type.label]));
            setTypeNames(newTypeMap);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des noms de types:", error);
        }
      }
    };

    loadTypeNames();
  }, [result.filters?.type]);

  // Effet pour charger les noms des origines quand les filtres changent
  useEffect(() => {
    const loadOriginNames = async () => {
      if (result.filters?.origin) {
        try {
          const originIds = Array.isArray(result.filters.origin) ? result.filters.origin : [result.filters.origin];
          const validOriginIds = originIds.filter(id => id && typeof id === "string");
          
          if (validOriginIds.length > 0) {
            const response = await searchWithFilters<EasyTech.WorkorderOrigin>("workorder_origins", {
              queries: [createListQuery("id", validOriginIds, "IN")]
            }, true, false);
            
            const origins = response.data as EasyTech.WorkorderOrigin[];
            const newOriginMap = new Map(origins.map(origin => [origin.id, origin.label]));
            setOriginNames(newOriginMap);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des noms d'origines:", error);
        }
      }
    };

    loadOriginNames();
  }, [result.filters?.origin]);

  return result;
} 